import React from 'react';
import { Image, FileText, Video } from 'lucide-react';

export function fillTemplateVariables(text, examples = []) {
  if (!text) return '';
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    const sample = examples[idx];
    return sample != null && String(sample).trim() ? String(sample) : `{{${n}}}`;
  });
}

const MediaPlaceholder = ({ type, previewUrl, fileName, savedOnMeta }) => {
  if (type === 'IMAGE' && previewUrl) {
    return (
      <img src={previewUrl} alt="" className="w-full h-32 object-cover bg-gray-100" />
    );
  }
  if (type === 'VIDEO' && previewUrl) {
    return (
      <video src={previewUrl} className="w-full h-32 object-cover bg-black" controls muted />
    );
  }
  if (type === 'DOCUMENT' && (previewUrl || fileName)) {
    return (
      <div className="bg-gray-100 h-24 flex items-center justify-center gap-2 px-3 text-gray-600 text-xs">
        <FileText className="h-6 w-6 shrink-0" />
        <span className="truncate">{fileName || 'PDF document'}</span>
      </div>
    );
  }
  if (savedOnMeta) {
    const Icon = type === 'VIDEO' ? Video : type === 'DOCUMENT' ? FileText : Image;
    return (
      <div className="bg-green-50 border border-green-100 h-32 flex flex-col items-center justify-center text-green-800 text-xs gap-1 px-3 text-center">
        <Icon className="h-8 w-8 opacity-70" />
        <span className="font-medium">Sample saved on Meta</span>
        <span className="text-green-600">{type} header</span>
      </div>
    );
  }

  const Icon = type === 'VIDEO' ? Video : type === 'DOCUMENT' ? FileText : Image;
  return (
    <div className="bg-gray-200 rounded-lg h-32 flex flex-col items-center justify-center text-gray-500 text-xs gap-1">
      <Icon className="h-8 w-8 opacity-60" />
      <span>{type} header</span>
    </div>
  );
};

const WhatsAppTemplatePreview = ({
  form,
  businessName = 'Your Business',
  compact = false,
  sidebar = false
}) => {
  const bodyExamples = form?.bodyExamples || [];
  const bodyText = fillTemplateVariables(form?.bodyText, bodyExamples);
  const headerText = fillTemplateVariables(form?.headerText, form?.headerExamples || []);
  const buttons = (form?.buttons || []).filter((b) => b?.text?.trim());

  const phoneMaxWidth = compact ? '100%' : sidebar ? 260 : 320;
  const phoneBorder = sidebar ? 'border-8' : 'border-[10px]';
  const phoneRadius = sidebar ? 'rounded-[1.75rem]' : 'rounded-[2rem]';

  return (
    <div className={compact ? 'w-full' : 'sticky top-4 w-full max-w-full'}>
      {!compact && (
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">WhatsApp preview</p>
      )}
      <div
        className={`mx-auto w-full ${phoneRadius} ${phoneBorder} border-gray-800 bg-[#e5ddd5] shadow-xl overflow-hidden`}
        style={{ maxWidth: phoneMaxWidth }}
      >
        <div className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold">
            {businessName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{businessName}</p>
            <p className="text-[10px] text-white/70">Business account</p>
          </div>
        </div>

        <div className="p-3 min-h-[200px] max-h-[420px] overflow-y-auto space-y-2">
          <div className="flex justify-start">
            <div className="bg-white rounded-lg rounded-tl-none shadow-sm max-w-[92%] overflow-hidden">
              {form?.headerType === 'TEXT' && headerText && (
                <p className="px-3 pt-2.5 text-sm font-semibold text-gray-900">{headerText}</p>
              )}
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form?.headerType) && (
                <div className={form?.headerPreviewUrl || form?.headerFileName ? '' : 'p-2 pb-0'}>
                  <MediaPlaceholder
                    type={form.headerType}
                    previewUrl={form.headerPreviewUrl}
                    fileName={form.headerFileName}
                    savedOnMeta={form.headerHandle && !form.headerPreviewUrl && form.headerMediaSource === 'existing'}
                  />
                </div>
              )}
              {bodyText ? (
                <p className="px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{bodyText}</p>
              ) : (
                <p className="px-3 py-3 text-sm text-gray-400 italic">Message body…</p>
              )}
              {form?.footerText && (
                <p className="px-3 pb-2 text-[11px] text-gray-500">{form.footerText}</p>
              )}
              <p className="px-3 pb-1.5 text-[10px] text-gray-400 text-right">
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              {buttons.length > 0 && (
                <div className="border-t border-gray-100">
                  {buttons.map((btn, i) => (
                    <div
                      key={i}
                      className="text-center text-[#00a5f4] text-sm py-2.5 border-b border-gray-100 last:border-b-0 font-medium"
                    >
                      {btn.type === 'URL' && '🔗 '}
                      {btn.type === 'PHONE_NUMBER' && '📞 '}
                      {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {form?.category && (
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Category: {form.category} · {form.language || 'en_US'}
        </p>
      )}
    </div>
  );
};

export function previewFromLibraryItem(lib) {
  const headerFormat = (lib.header_type || lib.header_format || '').toUpperCase();
  const hasTextHeader = lib.header && !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat);

  return {
    headerType: hasTextHeader ? 'TEXT' : headerFormat || 'none',
    headerText: hasTextHeader ? lib.header : '',
    bodyText: lib.body || '',
    bodyExamples: lib.body_params || [],
    footerText: lib.footer || '',
    buttons: (lib.buttons || []).map((b) => ({ type: b.type, text: b.text || '' })),
    category: lib.category,
    language: lib.language
  };
}

export function previewFromTemplate(template) {
  const components = template?.components || [];
  const header = components.find((c) => c.type === 'HEADER');
  const body = components.find((c) => c.type === 'BODY');
  const footer = components.find((c) => c.type === 'FOOTER');
  const buttonsComp = components.find((c) => c.type === 'BUTTONS');
  let headerType = 'none';
  let headerText = '';
  if (header) {
    headerType = header.format || 'TEXT';
    if (headerType === 'TEXT') headerText = header.text || '';
  }
  return {
    headerType,
    headerText,
    bodyText: body?.text || template?.content || '',
    footerText: footer?.text || '',
    buttons: (buttonsComp?.buttons || []).map((b) => ({ type: b.type, text: b.text || '' })),
    category: template?.category,
    language: template?.language
  };
}

export default WhatsAppTemplatePreview;
